import { classnames } from 'Utils/classnames/classnames.utils';
import { camelCase, isEmpty } from 'lodash';
import { useRouter } from 'next/router';
import React from 'react';
import { windowWrapper } from '@clubmed/utils';
import DynamicLinkUtil from 'Components/frameworks/dynamic-link/DynamicLinkUtil';
import type { RatioDisplay } from 'Components/frameworks/image/Image';
import { ICONS } from 'Components/utils/icon/icons';
import { scrollToElement } from 'Components/utils/scroll/scrollToElement';
import {
  ACTIONS,
  useCriteriaDisplayContext,
} from 'Components/widgets/criteria/CriteriaDisplayContext';
import type { ProductProps } from 'Components/widgets/product/Product.utils';
import {
  generateLinkProp,
  generateProductLink,
  generateCmtaLinkProp,
  localeHasCmtaLinks,
} from 'Components/widgets/product/Product.utils';
import { usePageContext } from 'Context/PageContext';
import { useAppContext } from 'Context/ReactAppContext';
import { getKeyValue, t } from 'Src/i18n';
import { useTracking } from 'Tracking/tracking-api/useTracking';
import { isFeatureFlipped } from 'Utils/feature-flipping/featureFlippingUtils';
import { Icon } from 'Components/frameworks/icon/Icon';
import { OfferDiscount } from 'Components/widgets/offer-discount/OfferDiscount';
import { ProductHeader } from 'Components/widgets/product/product-header/ProductHeader';
import { ProductComparison } from 'Components/widgets/product/product-comparison/ProductComparison';
import { ProductFlag } from 'Components/widgets/product/product-flag/ProductFlag';
import { ProductThumbnail } from 'Components/widgets/product/product-thumbnail/ProductThumbnail';
import { ProductChildcareAvailabilities } from 'Components/widgets/product/product-childcare-availabilities/productChildcareAvailabilities';
import { Tag } from 'Components/frameworks/tag/Tag';
type Props = ProductProps & {
  cmtaLinks?: Record<string, any>;
  index?: number;
  isCompact?: boolean;
  lineName?: string;
  offer?: Offer;
  onComparisonClick?: () => unknown;
  ratioDisplay?: RatioDisplay;
};
export const Product = ({
  childcare,
  childcareIncluded,
  className,
  cmtaLinks = {},
  comfort,
  comparison,
  departureDateOn,
  flag = {},
  hasComparison,
  hasDiscount,
  hasFadeIn = true,
  hasLazyLoading,
  header,
  id,
  index,
  isCompact = false,
  isLinkDisabled,
  kids = [],
  lineName,
  minAuthorizedAge = {},
  offer,
  offerDiscount,
  onClick,
  onComparisonClick,
  ratioDisplay,
  thumbnail,
  url,
}: Props) => {
  const appContext = useAppContext();
  const isGsa = isFeatureFlipped(appContext)('isGsa');
  const router = useRouter();
  const [, dispatchCriteriaDisplayContext] = useCriteriaDisplayContext();
  const track = useTracking({});
  const pageContext = usePageContext();
  const siteSection = pageContext?.siteSection || '';
  const clickThumbnailTrackingData =
    typeof index === 'number'
      ? {
          actionGA: 'clickThumbnail',
          labelGA: id,
          resortCode: id,
          resortTrident: typeof comfort === 'number' ? comfort : '',
          thumbnailPrice: flag?.price?.bestPrice?.value || '',
          thumbnailList: lineName ? `${siteSection}_${camelCase(lineName)}` : siteSection,
          thumbnailPosition: index + 1,
        }
      : null;
  const clickIconBubbleTrackingData =
    typeof index === 'number'
      ? {
          actionGA: 'clickThumbnail',
          labelGA: `${id}_infoIcon`,
          resortCode: id,
          thumbnailPrice: flag?.price?.bestPrice?.value || '',
          thumbnailList: lineName ? `${siteSection}_${camelCase(lineName)}` : siteSection,
          thumbnailPosition: index + 1,
          siteSection,
        }
      : null;
  const linkShouldBeDisabled = isLinkDisabled || hasComparison || isGsa;

  const goOnResort = () => {
    const newHistoryState = new DynamicLinkUtil(
      windowWrapper.getLocation(),
      url ? generateProductLink(url, offer) : '',
    );
    router.push(newHistoryState.toString());
  };

  const minAuthorizedAgeMessage =
    minAuthorizedAge?.value === 0
      ? t('faceting.availabilities.noChildcareFrom0')
      : t('faceting.availabilities.noChildcare', {
          min_authorized_age_in_months: Math.floor((minAuthorizedAge?.value ?? 0) / 12),
        });
  const childcareBirthdateAlert =
    kids?.length > 0 && childcare && isEmpty(childcare.items) && !childcareIncluded;
  const noChildcareAlert =
    kids && kids.length > 0 && kids.some((kid) => !kid.birthdate) && childcareIncluded;
  return (
    <article
      className={classnames(
        'relative isolate',
        {
          'animation-duration-750 animate-fadeIn': hasFadeIn,
        },
        className,
      )}
      onClick={onClick}
    >
      <div
        className={classnames('relative isolate', {
          'flex gap-10': isCompact,
        })}
      >
        <div
          className={classnames('relative', {
            'w-1/2 shrink-0': isCompact,
          })}
          data-testid="Product-Header-Wrapper"
        >
          {hasComparison && (
            <ProductComparison {...comparison} onComparisonClick={onComparisonClick} />
          )}

          {!!departureDateOn && (
            <Tag
              className="absolute start-0 z-2 mx-10 -translate-y-1/2 border-sienna bg-white text-sienna"
              data-testid="Product-departure"
            >
              {departureDateOn}
            </Tag>
          )}

          {hasDiscount && (
            <OfferDiscount
              className="absolute end-0 z-2 mx-10 -translate-y-1/2"
              discount={offerDiscount?.discount}
              i18n={offerDiscount?.i18n}
            />
          )}

          <ProductThumbnail
            {...thumbnail}
            className="overflow-hidden rounded-12"
            hasLazyLoading={hasLazyLoading}
            isLinkDisabled
            nextLabel={t('common.next')}
            previousLabel={t('common.previous')}
            ratioDisplay={ratioDisplay}
          />
        </div>
        <ProductHeader
          {...header}
          className={classnames({
            'overflow-hidden': isCompact,
            'mt-10': !isCompact,
          })}
          isLinkDisabled={linkShouldBeDisabled}
          link={
            appContext.isCmta && id && localeHasCmtaLinks(cmtaLinks)
              ? generateCmtaLinkProp(id, cmtaLinks)
              : generateLinkProp(url, offer)
          }
          onClick={() => {
            if (clickThumbnailTrackingData) {
              track(clickThumbnailTrackingData);
            }
          }}
        />
      </div>

      {!isEmpty(flag) && (
        <ProductFlag
          {...flag}
          isLinkDisabled={linkShouldBeDisabled}
          onClick={goOnResort}
          onCtaClick={() => {
            if (clickThumbnailTrackingData) {
              track({ ...clickThumbnailTrackingData, labelGA: `${id}_discoverResort` });
            }
          }}
          onEmptyPriceClick={() => {
            if (clickThumbnailTrackingData) {
              track(clickThumbnailTrackingData);
            }
          }}
          onPriceClick={() => {
            if (clickThumbnailTrackingData) {
              track({ ...clickThumbnailTrackingData, labelGA: `${id}_bestPrice` });
            }
          }}
          onIconBubbleClick={() => {
            if (getKeyValue('price.displayIconBubble')) {
              track({ ...clickIconBubbleTrackingData, labelGA: `${id}_infoIcon` });
            }
          }}
          price={flag.price}
        />
      )}

      {childcareBirthdateAlert && (
        <div className="mt-5 flex gap-5 rounded-12 border-1 border-lightGrey p-10 text-12">
          <Icon className="mt-1 shrink-0 text-red" name={ICONS.WARNING} width="1rem" />
          {minAuthorizedAgeMessage}
        </div>
      )}

      {noChildcareAlert && (
        <button
          className="text-initial mt-5 flex cursor-pointer gap-5 rounded-12 bg-sand p-10 text-12 font-bold leading-1 text-black transition-colors duration-250 ease-in hocus:bg-sand-active"
          onClick={() => {
            dispatchCriteriaDisplayContext({
              type: ACTIONS.OPEN_CRITERIA,
              payload: 'criteriaWho',
            });
            scrollToElement('[data-testid="CriteriaWho"]', 600);
          }}
          type="button"
        >
          <Icon className="mt-1 shrink-0" name={ICONS.KIDS} width="1rem" />
          {t('faceting.availabilities.enterBirthdate')}
        </button>
      )}

      {isFeatureFlipped(appContext)('faceting.childcare') &&
        childcare &&
        !isEmpty(childcare.items) && <ProductChildcareAvailabilities {...childcare} />}
    </article>
  );
};
